import React, { useEffect, useState } from 'react';

import {
  Grid,
  Card,
  CardContent,
  Typography,
  makeStyles,
  Button,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloudOffIcon from '@material-ui/icons/CloudOff';
import ErrorIcon from '@material-ui/icons/Error';
import HelpIcon from '@material-ui/icons/Help';
import ScheduleIcon from '@material-ui/icons/Schedule';
import SyncIcon from '@material-ui/icons/Sync';
import WarningIcon from '@material-ui/icons/Warning';
import { KeyboardDatePicker } from '@material-ui/pickers';

import { api } from '../../../services';
import {
  stringify,
  STATUS_EMISSAO_NFE_PENDENTE,
  STATUS_EMISSAO_NFE_AUTORIZADA,
  STATUS_EMISSAO_NFE_AUTORIZADA_SEM_XML,
  STATUS_EMISSAO_NFE_CANCELADA,
  STATUS_EMISSAO_NFE_CONTINGENCIA,
  STATUS_EMISSAO_NFE_CONTINGENCIA_ENVIADA,
  STATUS_EMISSAO_NFE_DENEGADA,
  STATUS_EMISSAO_NFE_INDEFINIDA,
  STATUS_EMISSAO_NFE_PROCESSANDO,
  STATUS_EMISSAO_NFE_REJEITADA,
  STATUS_EMISSAO_NFE_REJEITADA_DEFINITIVO,
  STATUS_EMISSAO_NFE_DESCONHECIDO,
  STATUS_EMISSAO_NFE_REJEITADA_REPROCESSAMENTO,
} from '../../../utils';
import { PERMISSAO_TELA_INDICADORES_NFE_LISTAR } from '../../../utils/dadosTelas';
import { ProtegerPagina } from '../../VerificaPermissaoPage';

const useStyles = makeStyles((theme) => ({
  card: {
    cursor: 'pointer',
    transition: '0.2s',
    borderRadius: 12,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
    },
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: 13,
    color: '#777',
  },
  count: {
    fontSize: 24,
    fontWeight: 600,
  },
  total: {
    fontSize: 13,
    color: '#999',
  },
  icon: {
    fontSize: 36,
    opacity: 0.3,
  },
}));

const resumo = [
  // 🟡 PROCESSAMENTO
  {
    status: STATUS_EMISSAO_NFE_PENDENTE,
    label: 'Pendentes',
    count: 10,
    total: 1200,
    color: '#ff9800',
    icon: <ScheduleIcon />,
  },
  {
    status: STATUS_EMISSAO_NFE_PROCESSANDO,
    label: 'Processando',
    count: 6,
    total: 800,
    color: '#ffc107',
    icon: <SyncIcon />,
  },

  // 🟢 SUCESSO
  {
    status: STATUS_EMISSAO_NFE_AUTORIZADA,
    label: 'Autorizadas',
    count: 120,
    total: 15420.55,
    color: '#4caf50',
    icon: <CheckCircleIcon />,
  },
  {
    status: STATUS_EMISSAO_NFE_AUTORIZADA_SEM_XML,
    label: 'Autorizadas sem XML',
    count: 2,
    total: 300,
    color: '#66bb6a',
    icon: <WarningIcon />,
  },

  // 🔴 ERROS
  {
    status: STATUS_EMISSAO_NFE_REJEITADA,
    label: 'Rejeitadas',
    count: 8,
    total: 0,
    color: '#f44336',
    icon: <ErrorIcon />,
  },
  {
    status: STATUS_EMISSAO_NFE_REJEITADA_DEFINITIVO,
    label: 'Rejeitadas (Definitivo)',
    count: 3,
    total: 0,
    color: '#b71c1c',
    icon: <ErrorIcon />,
  },
  {
    status: STATUS_EMISSAO_NFE_REJEITADA_REPROCESSAMENTO,
    label: 'Rejeitadas (Reprocesso)',
    count: 4,
    total: 0,
    color: '#e57373',
    icon: <SyncIcon />,
  },
  {
    status: STATUS_EMISSAO_NFE_DENEGADA,
    label: 'Denegadas',
    count: 1,
    total: 0,
    color: '#d32f2f',
    icon: <CancelIcon />,
  },

  // ⚫ CANCELAMENTO
  {
    status: STATUS_EMISSAO_NFE_CANCELADA,
    label: 'Canceladas',
    count: 5,
    total: 800,
    color: '#9e9e9e',
    icon: <CancelIcon />,
  },

  // 🔵 CONTINGÊNCIA
  {
    status: STATUS_EMISSAO_NFE_CONTINGENCIA,
    label: 'Contingência',
    count: 2,
    total: 500,
    color: '#03a9f4',
    icon: <CloudOffIcon />,
  },
  {
    status: STATUS_EMISSAO_NFE_CONTINGENCIA_ENVIADA,
    label: 'Contingência Enviada',
    count: 1,
    total: 200,
    color: '#0288d1',
    icon: <CloudOffIcon />,
  },

  // ❓ OUTROS
  {
    status: STATUS_EMISSAO_NFE_INDEFINIDA,
    label: 'Indefinidas',
    count: 1,
    total: 0,
    color: '#607d8b',
    icon: <HelpIcon />,
  },
  {
    status: STATUS_EMISSAO_NFE_DESCONHECIDO,
    label: 'Desconhecidas',
    count: 1,
    total: 0,
    color: '#455a64',
    icon: <HelpIcon />,
  },
];

function FiltroPeriodo({ onChange }) {
  const [dataInicio, setDataInicio] = useState(new Date());
  const [dataFim, setDataFim] = useState(new Date());

  const aplicarFiltro = () => {
    onChange({
      start: dataInicio,
      end: dataFim,
    });
  };

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sm={4}>
        <KeyboardDatePicker
          fullWidth
          format="dd/MM/yyyy"
          label="Data Inicial"
          value={dataInicio}
          onChange={setDataInicio}
          inputVariant="outlined"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <KeyboardDatePicker
          fullWidth
          format="dd/MM/yyyy"
          label="Data Final"
          value={dataFim}
          onChange={setDataFim}
          inputVariant="outlined"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={aplicarFiltro}
          style={{ height: 56 }}
        >
          Aplicar
        </Button>
      </Grid>
    </Grid>
  );
}

function DashboardNotas() {
  const classes = useStyles();
  const [filtro, setFiltro] = useState(null);

  const [dados, setDados] = useState([]);
  const [periodo, setPeriodo] = useState(null);

  const handleClick = (status) => {
    setFiltro(status);
  };

  useEffect(() => {
    async function getDados() {
      const response = await api.get(
        `/indicadores_nfe?${stringify({
          periodo: JSON.stringify(periodo),
        })}`,
      );

      const newResumo = [];

      for (let i = 0; i < resumo.length; i++) {
        const element = response.data[resumo[i].status];
        newResumo.push({
          ...resumo[i],
          count: element ? element.count : 0,
          total: element ? element.total : 0,
        });
      }

      setDados(newResumo);
    }

    getDados();
  }, [periodo]);

  return (
    <div style={{ padding: 20 }}>
      <Typography variant="h6" style={{ marginBottom: 16 }}>
        Resumo NF-e por Status
      </Typography>

      <FiltroPeriodo
        onChange={(periodo) => {
          setPeriodo(periodo);
        }}
      />

      {filtro && (
        <Typography style={{ marginBottom: 10 }}>
          Filtro ativo: <strong>{filtro}</strong>
        </Typography>
      )}

      <Grid container spacing={2}>
        {dados.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.status}>
            <Card
              className={classes.card}
              onClick={() => handleClick(item.status)}
              style={{
                borderLeft: `6px solid ${item.color}`,
                background:
                  filtro === item.status ? 'rgba(0,0,0,0.05)' : '#fff',
              }}
            >
              <CardContent className={classes.content}>
                <div className={classes.left}>
                  <Typography className={classes.label}>
                    {item.label}
                  </Typography>

                  <Typography className={classes.count}>
                    {item.count}
                  </Typography>

                  <Typography className={classes.total}>
                    R$ {item.total.toFixed(2)}
                  </Typography>
                </div>

                <div className={classes.icon} style={{ color: item.color }}>
                  {item.icon}
                </div>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
}

function Wrapper() {
  return (
    <ProtegerPagina permissao={PERMISSAO_TELA_INDICADORES_NFE_LISTAR}>
      <DashboardNotas />
    </ProtegerPagina>
  );
}

export default Wrapper;
